/**
 * One-line “why this transition works” — mirrors backend TransitionNarrative.java for drag-reorder.
 */

export type SetProfileNarrative = "SMOOTH" | "ENERGETIC" | "WILD";

export interface NarrativeTrackSlice {
  bpm: number | null;
  camelotCode: string | null;
  energyLevel: number | null;
  danceability: number | null;
}

function camelotClause(from: NarrativeTrackSlice, to: NarrativeTrackSlice, camelotDist: number): string {
  const fk = from.camelotCode?.trim() || "?";
  const tk = to.camelotCode?.trim() || "?";
  if (fk === "?" || tk === "?") {
    return "Key metadata missing — mix by ear and check harmonics in the booth";
  }
  const Fu = fk.toUpperCase();
  const Tu = tk.toUpperCase();
  if (Fu === Tu) {
    return `${Fu}→${Tu} (perfect Camelot match — same harmonic slot)`;
  }
  if (camelotDist <= 1) {
    return `${Fu}→${Tu} (perfect Camelot transition on the wheel)`;
  }
  if (camelotDist <= 3) {
    return `${Fu}→${Tu} (harmonically compatible — keep the blend tight)`;
  }
  return `${Fu}→${Tu} (harmonic tension — short blend or EQ to ease the clash)`;
}

function bpmClause(
  from: NarrativeTrackSlice,
  to: NarrativeTrackSlice,
  bpmDeltaAbs: number,
  profile: SetProfileNarrative
): string {
  const bpmA = from.bpm ?? 128;
  const bpmB = to.bpm ?? 128;
  const bpmDir = bpmB - bpmA;
  if (bpmDeltaAbs < 0.5) {
    return "same BPM — easy tempo lock";
  }
  const step = Math.round(bpmDir);
  const signed = step > 0 ? `+${step}` : String(step);
  const gap = Math.round(bpmDeltaAbs);

  if (profile === "WILD") {
    if (bpmDeltaAbs <= 3) return `BPM ${signed} — sharp ${step > 0 ? "lift" : "pull-back"}`;
    if (bpmDeltaAbs <= 8) return `~${gap} BPM apart — ride the energy, shorter overlap`;
    if (bpmDeltaAbs <= 15) return `~${gap} BPM gap — filter / tempo ride territory`;
    return `~${gap} BPM gap — echo out or planned tempo change`;
  }
  if (profile === "ENERGETIC") {
    if (bpmDeltaAbs <= 3) {
      return `BPM ${signed} for an energetic ${step > 0 ? "build" : "breather"}`;
    }
    if (bpmDeltaAbs <= 8) return `~${gap} BPM apart — punchy 16-bar style blend`;
    if (bpmDeltaAbs <= 15) return `~${gap} BPM gap — sweep or nudge tempo before the drop`;
    return `large ~${gap} BPM gap — carve space then re-introduce`;
  }
  if (bpmDeltaAbs <= 3) {
    return `BPM ${signed} for a smooth ${step > 0 ? "build" : "cool-down"}`;
  }
  if (bpmDeltaAbs <= 8) return `~${gap} BPM apart — gentle overlap or subtle ride`;
  if (bpmDeltaAbs <= 15) return `~${gap} BPM gap — filter sweep / soft tempo nudge`;
  return `large ~${gap} BPM gap — plan an echo-out or tempo bridge`;
}

function isGenericEnergy(s: string): boolean {
  return s === "holding energy level" || s === "both tracks pegged high (loudness)";
}

function isGenericDance(s: string): boolean {
  return s === "steady groove";
}

function energyNarrative(a: number, b: number, profile: SetProfileNarrative): string {
  const jump = b - a;
  const abs = Math.abs(jump);
  if (abs < 0.02) {
    if (a >= 0.92 && b >= 0.92) return "both tracks pegged high (loudness)";
    return "holding energy level";
  }
  if (abs < 0.08) {
    if (jump > 0) return profile === "WILD" ? "quick energy spike" : "slight energy lift";
    return "subtle energy dip";
  }
  if (abs < 0.18) {
    return jump > 0
      ? profile === "SMOOTH"
        ? "moderate energy build"
        : "clear energy step up"
      : "noticeable cool-down in the room";
  }
  return jump > 0
    ? profile === "WILD"
      ? "big energy leap — slam the moment"
      : "strong energy lift into the next section"
    : "deep energy drop — breakdown or palette reset";
}

function danceNarrative(a: number, b: number, profile: SetProfileNarrative): string {
  const jump = b - a;
  const abs = Math.abs(jump);
  if (abs < 0.02) return "steady groove";
  if (abs < 0.1) return jump > 0 ? "groove opens a little" : "groove tucks in";
  if (abs < 0.2) return jump > 0 ? "dancefloor drive picks up" : "dancefloor eases back";
  return jump > 0
    ? profile === "WILD"
      ? "heavy groove injection"
      : "big groove lift"
    : "strip groove back for space";
}

function energyGrooveClause(
  from: NarrativeTrackSlice,
  to: NarrativeTrackSlice,
  profile: SetProfileNarrative
): string {
  const fromE = from.energyLevel != null;
  const toE = to.energyLevel != null;
  const fromD = from.danceability != null;
  const toD = to.danceability != null;

  let energy = "";
  if (fromE && toE) {
    energy = energyNarrative(from.energyLevel!, to.energyLevel!, profile);
  }
  let dance = "";
  if (fromD && toD) {
    dance = danceNarrative(from.danceability!, to.danceability!, profile);
  }

  if (energy && !isGenericEnergy(energy)) return energy;
  if (dance && !isGenericDance(dance)) return dance;
  if (energy) return energy;
  if (dance) return dance;
  return "";
}

export function parseSetProfileNarrative(raw: string): SetProfileNarrative {
  if (raw === "ENERGETIC" || raw === "WILD") return raw;
  return "SMOOTH";
}

export function buildWhyLine(
  from: NarrativeTrackSlice,
  to: NarrativeTrackSlice,
  profile: SetProfileNarrative,
  bpmDeltaAbs: number,
  camelotDistance: number
): string {
  const parts: string[] = [];
  const c = camelotClause(from, to, camelotDistance);
  if (c) parts.push(c);
  const b = bpmClause(from, to, bpmDeltaAbs, profile);
  if (b) parts.push(b);
  const v = energyGrooveClause(from, to, profile);
  if (v) parts.push(v);
  if (parts.length === 0) return "Balanced transition — trust your ears in the headphones.";
  return parts.join(", ");
}
